import React from "react";
import { Check, Lock, Plus, StickyNote, User, UserX, Video } from "lucide-react";
import { cn } from "../../../utils/cn";
import ScheduleType from "../ScheduleType";
import { ResolvedScheduleCell, SlotVisualKind } from "../scheduleGridTypes";
import {
  SCHEDULE_GRID_CELL_HEIGHT_PX,
  SLOT_THEME,
} from "../scheduleGridTheme";

type SlotCellProps = {
  cell: ResolvedScheduleCell;
  isSelected: boolean;
  isCurrentHour: boolean;
  isWeatherHighlighted?: boolean;
  isWeatherRiskHour?: boolean;
  canInteract: boolean;
  onMouseDown: (event: React.MouseEvent) => void;
  onMouseOver?: (event: React.MouseEvent) => void;
  onOpenDetails: () => void;
};

function isInteractiveKind(kind: SlotVisualKind): boolean {
  return kind !== "empty";
}

const SlotCell: React.FC<SlotCellProps> = ({
  cell,
  isSelected,
  isCurrentHour,
  isWeatherHighlighted = false,
  isWeatherRiskHour = false,
  canInteract,
  onMouseDown,
  onMouseOver,
  onOpenDetails,
}) => {
  const displayKind = cell.visualKind;
  const theme = SLOT_THEME[displayKind];
  const isScheduled = cell.displayStatus === ScheduleType.SCHEDULED;
  const isFreeSlot = displayKind === "free";
  const isEmpty = displayKind === "empty";
  const isVisitKind =
    displayKind === "scheduled" ||
    displayKind === "video" ||
    displayKind === "mixed";

  const iconColor =
    displayKind === "video"
      ? "text-[#2C5288]"
      : displayKind === "mixed"
        ? "text-[#234E52]"
        : displayKind === "scheduled"
          ? "text-[#166534]"
          : displayKind === "completed"
            ? "text-white"
            : displayKind === "no_show"
              ? "text-[#9C4221]"
              : displayKind === "blocked"
                ? "text-[#718096]"
                : "text-[#A0AEC0]";

  function handleClick(event: React.MouseEvent) {
    if (isScheduled) {
      event.stopPropagation();
      onOpenDetails();
    }
  }

  const title =
    cell.clientSummary.tooltip ||
    cell.blockedNote ||
    (isFreeSlot ? "Horário livre" : undefined);

  function renderTopIcon() {
    if (displayKind === "blocked") {
      return <Lock className={cn("h-3.5 w-3.5 shrink-0", iconColor)} aria-hidden />;
    }
    if (isFreeSlot) {
      return <Plus className={cn("h-3.5 w-3.5 shrink-0", iconColor)} aria-hidden />;
    }
    if (displayKind === "completed" && isScheduled) {
      return <Check className={cn("h-3.5 w-3.5 shrink-0 opacity-90", iconColor)} aria-hidden />;
    }
    if (displayKind === "no_show" && isScheduled) {
      return <UserX className={cn("h-3.5 w-3.5 shrink-0 opacity-90", iconColor)} aria-hidden />;
    }
    if (isVisitKind && isScheduled) {
      if (displayKind === "video" || displayKind === "mixed") {
        return <Video className={cn("h-3.5 w-3.5 shrink-0 opacity-90", iconColor)} aria-hidden />;
      }
      return <User className={cn("h-3.5 w-3.5 shrink-0 opacity-75", iconColor)} aria-hidden />;
    }
    return <span className="h-3.5 w-3.5" aria-hidden />;
  }

  function renderBottomLabel() {
    if (displayKind === "blocked") {
      return cell.blockedNote ?? "Bloqueado";
    }
    if (isFreeSlot) {
      return "Livre";
    }
    if (displayKind === "completed" && isScheduled) {
      return cell.clientSummary.label || "Realizada";
    }
    if (displayKind === "no_show" && isScheduled) {
      return cell.clientSummary.label || "Não apareceu";
    }
    if (isVisitKind && isScheduled) {
      return cell.clientSummary.label || "Agendada";
    }
    return "";
  }

  const bottomLabel = renderBottomLabel();
  const showStructuredLayout = !isEmpty && bottomLabel.length > 0;

  return (
    <button
      type="button"
      className={cn(
        "relative w-full rounded-md text-left transition-[border-color,background-color,box-shadow] duration-150",
        showStructuredLayout
          ? "flex flex-col justify-between px-1.5 py-1.5"
          : "flex flex-col items-center justify-center px-0.5 py-0.5 text-center",
        theme.shell,
        isVisitKind && isScheduled && "hover:border-[#276749]",
        isFreeSlot && canInteract && "hover:border-[#c4552b]/55 hover:bg-[#FFFAF7]",
        displayKind === "blocked" && "hover:border-[#CBD5E0]",
        isSelected && "ring-2 ring-[#c4552b] ring-offset-1",
        isCurrentHour &&
          "shadow-[inset_0_0_0_1.5px_rgba(196,85,43,0.55)] ring-1 ring-[#c4552b]/25 ring-inset",
        isWeatherRiskHour && "ring-1 ring-sky-300 ring-inset",
        isWeatherHighlighted && "ring-2 ring-sky-500 ring-offset-1",
        (canInteract && isInteractiveKind(displayKind)) || isScheduled
          ? "cursor-pointer"
          : "cursor-default",
        isEmpty && "pointer-events-none opacity-40"
      )}
      style={{
        minHeight: SCHEDULE_GRID_CELL_HEIGHT_PX,
        height: SCHEDULE_GRID_CELL_HEIGHT_PX,
      }}
      title={title}
      aria-label={title ?? displayKind}
      disabled={isEmpty}
      onMouseDown={canInteract ? onMouseDown : undefined}
      onMouseOver={canInteract ? onMouseOver : undefined}
      onClick={handleClick}
    >
      {showStructuredLayout ? (
        <>
          <div className="flex w-full items-start justify-between gap-1">
            {renderTopIcon()}
            {isVisitKind && isScheduled && cell.clientSummary.badge ? (
              <span className="flex shrink-0 items-center gap-1">
                {cell.hasNote ? (
                  <StickyNote
                    className={cn("h-3 w-3", iconColor)}
                    aria-label="Possui anotação"
                    title="Possui anotação"
                  />
                ) : null}
                <span
                  className={cn(
                    "rounded px-1 py-0.5 text-[9px] font-bold leading-none",
                    theme.badge
                  )}
                >
                  {cell.clientSummary.badge}
                </span>
              </span>
            ) : (
              cell.hasNote ? (
                <StickyNote
                  className={cn("h-3.5 w-3.5 shrink-0", iconColor)}
                  aria-label="Possui anotação"
                  title="Possui anotação"
                />
              ) : (
                <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
              )
            )}
          </div>
          <span
            className={cn(
              "mt-0.5 line-clamp-2 w-full break-words text-[12px] font-medium leading-snug",
              theme.label
            )}
          >
            {bottomLabel}
          </span>
        </>
      ) : null}
    </button>
  );
};

export default SlotCell;
